'use client';

import Link from 'next/link';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
	AlignLeft,
	ArrowDownWideNarrow,
	ArrowUpDown,
	ArrowUpNarrowWide,
	Award,
	BookMarked,
	Building2,
	ChevronDown,
	Eraser,
	Fingerprint,
	GraduationCap,
	Image as ImageIcon,
	Landmark,
	PenLine,
	ShieldCheck,
	ChevronLeft,
	ChevronRight,
	LayoutGrid,
	List,
	Search,
	SlidersHorizontal,
	TableProperties,
	UserRound,
	X,
} from 'lucide-react';
import type { InvestigatorRecord, ResearchLineRecord } from '@/data/researchCatalog';
import { ProfilePhotoImg } from '@/components/investigador/ProfilePhotoImg';
import { TipoInvestigadorBadge } from './TipoInvestigadorBadge';
import {
	ZOOM_DEFAULT,
	ZOOM_MAX,
	ZOOM_MIN,
	ZOOM_STEP,
	getDirectoryCardMetrics,
	type DirectoryCardMetrics,
} from './directory-card-metrics';
import { nombreMacrounidad, nombreUnidad } from '@/lib/unidad-display';
import { scoreNormalizedFuzzySearch } from '@/lib/search';
import { SelectInput, type SelectOption } from '@/components/ui/SelectInput';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { myProfileHref } from '@/hooks/useMyProfileHref';
import { useTopbarPortalTarget } from '@/hooks/useTopbarPortalTarget';
import { useSidePanel } from '@/components/layout/SidePanelContext';
import { usePortalTarget } from '@/hooks/usePortalTarget';
import { useIsMobile } from '@/hooks/useIsMobile';
import { SIDE_PANEL_BODY_PORTAL_ID } from '@/components/layout/SidePanelContext';
import sidebarStyles from '@/components/investigador/sidebar/sidebar.module.css';
import {
	coincideConFiltros,
	investigadoresEnAlcance,
	leyendaDeAlcance,
	opcionesDeLinea,
	opcionesDeLineaPersonalizada,
	resolverCascada,
	textosDeLineasParaBuscar,
	tieneAlgunaLinea,
	tieneLineaPersonalizada,
	unidadesDeFacultad,
} from './lib/directoryScope';

type DirectoryView = 'grid' | 'table' | 'compact';

/**
 * Cuántas tarjetas se listan por página. NO tienen que ser múltiplos del número
 * de columnas: la grilla es `auto-fill`, así que las columnas cambian con el
 * ancho y el zoom (5 al 100 %, 7 al 70 %, 2 al 200 %) y ningún número fijo
 * llenaría la última fila en todos los casos. Una fila final incompleta no
 * rompe nada — el virtualizador la mide igual que a las demás.
 */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 0] as const;

/** Criterios de orden. `apellido` no compara texto: respeta el orden en que llega
 *  el directorio, que la API ya entrega alfabético por apellido. */
const SORT_FIELDS = [
	{ value: 'nombre', label: 'Nombre' },
	{ value: 'apellido', label: 'Apellido' },
	{ value: 'unidad', label: 'Unidad' },
	{ value: 'facultad', label: 'Facultad' },
	{ value: 'estamento', label: 'Estamento' },
	{ value: 'publicaciones', label: 'Publicaciones' },
	{ value: 'ultimaPublicacion', label: 'Última publicación' },
] as const;

/** Criterios que dependen de datos de producción: se ofrecen sólo cuando ALGÚN
 *  perfil los tiene. Un orden que deja la lista igual porque todos valen cero es
 *  un control que miente. */
const SORT_FIELDS_NEED_PUBLICATIONS: readonly string[] = ['publicaciones', 'ultimaPublicacion'];

/**
 * Cuántas tarjetas se sirven ya renderizadas en el HTML, antes de que el
 * virtualizador tome el control. Medido sobre el HTML real de producción: 30
 * tarjetas más el índice de enlaces llevan la página de 20,0 a 26,6 KB gzip. Es
 * página ISR (`revalidate = 3600`), así que ese render se paga una vez por hora,
 * no por visita.
 */
const SSR_CARD_COUNT = 30;

/**
 * Filtros de "qué tiene cargado este perfil". Son una lista y no un booleano por
 * filtro porque van a seguir creciendo a medida que se pueble la base, y cada uno
 * suelto significaba tocar cinco lugares: el estado, el contador, el limpiar, el
 * chip y el checkbox.
 *
 * `has` decide DOS cosas con una sola definición: si el perfil pasa el filtro, y
 * si el filtro se ofrece siquiera. `adminOnly` los reserva a la gestión: hoy TODOS
 * lo son. Son filtros sobre el estado de carga de cada ficha —tiene foto, tiene
 * biografía—, que a quien busca a una persona no le sirven para encontrarla y de
 * paso exponen qué perfiles están a medio completar. Los que valgan para el
 * público se irán abriendo uno por uno, con el uso real como criterio. Un filtro que hoy no tiene a nadie que ofrecer
 * —los perfiles migrados de RR.HH. llegaron sin foto, sin biografía y sin
 * publicaciones— no se muestra: sería un control que sólo sabe vaciar la lista.
 * Cuando se carguen datos, aparece solo.
 */
/* `label` es la frase del checkbox en el panel ("Sólo con biografía"); `chip` es
   el sustantivo que va en la fila ("Biografía"), con mayúscula inicial como los
   filtros de selección — mezclados, "Facultad" junto a "Con biografía" se leían
   como dos clases de cosa. */
const CONTENT_FILTERS = [
	{
		key: 'orcid',
		adminOnly: true,
		label: 'Sólo con ORCID',
		chip: 'ORCID',
		Icon: Fingerprint,
		has: (i: InvestigatorRecord) => Boolean(i.orcid),
	},
	{
		key: 'wos',
		adminOnly: true,
		label: 'Sólo con ID WoS',
		chip: 'ID WoS',
		Icon: BookMarked,
		has: (i: InvestigatorRecord) => Boolean(i.wosResearcherId),
	},
	{
		key: 'scopus',
		adminOnly: true,
		label: 'Sólo con ID Scopus',
		chip: 'ID Scopus',
		Icon: BookMarked,
		has: (i: InvestigatorRecord) => Boolean(i.scopusAuthorId),
	},
	{
		key: 'publicaciones',
		adminOnly: true,
		label: 'Sólo con publicaciones',
		chip: 'Publicaciones',
		Icon: TableProperties,
		has: (i: InvestigatorRecord) => (i.publicacionesCount ?? 0) > 0,
	},
	{
		key: 'lineas',
		adminOnly: true,
		label: 'Sólo con líneas',
		chip: 'Líneas',
		Icon: GraduationCap,
		// Institucionales O personalizadas (R66): quien sólo cargó líneas propias también "tiene líneas".
		has: (i: InvestigatorRecord) => tieneAlgunaLinea(i),
	},
	{
		key: 'biografia',
		adminOnly: true,
		label: 'Sólo con biografía',
		chip: 'Biografía',
		Icon: AlignLeft,
		has: (i: InvestigatorRecord) => Boolean(i.tieneBiografia),
	},
	{
		key: 'foto',
		adminOnly: true,
		label: 'Sólo con foto',
		chip: 'Foto',
		Icon: ImageIcon,
		has: (i: InvestigatorRecord) => Boolean(i.fotoPerfilUrl),
	},
	{
		key: 'validado',
		adminOnly: true,
		label: 'Sólo perfiles validados',
		chip: 'Validados',
		Icon: ShieldCheck,
		has: (i: InvestigatorRecord) => Boolean(i.perfilValidado),
	},
] as const;

type ContentFilterKey = (typeof CONTENT_FILTERS)[number]['key'];

/**
 * Alto común de TODO lo que se apoya en la barra: buscador, orden, dirección,
 * vistas, el botón de filtros y los filtros aplicados de la segunda fila. Antes
 * cada control traía el suyo y la fila se leía como piezas sueltas apiladas en vez
 * de como una barra. `box-border`, así el borde no desalinea nada.
 */
const CONTROL_CLASS = 'h-[var(--siiv-control-height,36px)] box-border';

/**
 * Los estamentos EN SU ORDEN, que no es el alfabético.
 *
 * Ordenar por estamento alfabéticamente devolvía académico, directivo,
 * funcionario: un orden que no significa nada para nadie. El que la gente espera
 * es el de la estructura — directivo, académico, funcionario, estudiante — y no
 * sale de la base: `estamento` guarda `slug` y `nombre`, sin ningún campo de
 * jerarquía, y el `id` es orden de inserción, no un contrato.
 *
 * Se compara por SLUG y no por el nombre visible, que es lo que la base declara
 * estable. Un estamento que no esté acá cae al final, ordenado alfabéticamente
 * entre sus pares: es lo peor que puede pasar si mañana se agrega uno, y no
 * rompe nada.
 */
const ESTAMENTO_ORDER: readonly string[] = ['directivo', 'academico', 'funcionario', 'estudiante'];

const estamentoRank = (slug: string | null | undefined) => {
	const index = ESTAMENTO_ORDER.indexOf(slug ?? '');
	return index === -1 ? ESTAMENTO_ORDER.length : index;
};

type SortField = (typeof SORT_FIELDS)[number]['value'];
type SortDirection = 'asc' | 'desc';
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

/**
 * Ancho mínimo (px) del menú de las dos listas de líneas. El disparador mide una quinta parte de la
 * fila: sin esto, un nombre de línea de 90 caracteres se cortaba a media palabra en una caja de
 * 200 px. Con 420 se lee casi entero y lo que sobra lo recorta la elipsis.
 */
const LINE_MENU_MIN_WIDTH = 420;

/** Un filtro de selección: se declara una vez y lo pintan el panel y la fila de filtros aplicados. */
interface SelectFilterDef {
	key: string;
	Icon: ComponentType<{ className?: string }>;
	label: string;
	allLabel: string;
	value: string;
	onChange: (value: string) => void;
	options: SelectOption[];
	hint?: string;
	/** Qué decir cuando no hay opciones, en vez de mostrar un selector que parece roto. */
	emptyHint?: string;
	/** Cómo se pinta cada opción del menú; sin esto, el texto de la etiqueta. */
	renderOption?: (option: SelectOption) => ReactNode;
	/** Texto sobre el que busca el menú cuando hay muchas opciones; sin esto, la etiqueta. */
	getOptionSearchText?: (option: SelectOption) => string;
	menuMinWidth?: number;
}

interface InvestigadoresDirectoryClientProps {
	investigators: InvestigatorRecord[];
	researchLines: ResearchLineRecord[];
}

export function InvestigadoresDirectoryClient({
	investigators,
	researchLines,
}: InvestigadoresDirectoryClientProps) {
	const { user, status } = useAuthSession();
	const portalTarget = useTopbarPortalTarget();
	// El carril derecho del shell hospeda los filtros y el zoom; la vista sólo
	// declara que tiene panel y cuelga su contenido de los huecos que dejó.
	// `toggle`: no ocupa nada cerrado. Al abrirse empuja la grilla en escritorio
	// —hay ancho de sobra y tapar la lista que estás filtrando sería peor— y se
	// superpone a ancho completo en pantallas angostas, donde no hay nada que ceder.
	const sidePanel = useSidePanel('Filtros', 'toggle');
	const panelTarget = usePortalTarget(SIDE_PANEL_BODY_PORTAL_ID);
	const isMobile = useIsMobile();
	const [view, setView] = useState<DirectoryView>('grid');
	const [zoom, setZoom] = useState(ZOOM_DEFAULT);
	const [search, setSearch] = useState('');
	const [facultyFilter, setFacultyFilter] = useState('all');
	const [unitFilter, setUnitFilter] = useState('all');
	const [lineFilter, setLineFilter] = useState('all');
	/** Clave normalizada (sin mayúsculas ni tildes) de la línea personalizada elegida. */
	const [customLineFilter, setCustomLineFilter] = useState('all');
	const [estamentoFilter, setEstamentoFilter] = useState('all');
	/**
	 * Filtros de sí/no, con TRES estados y no dos.
	 *
	 * Ausente = no está puesto. `true` = puesto y aplicando. `false` = puesto pero
	 * apagado: sigue a la vista en la fila y se vuelve a encender de un clic. Ese
	 * tercer estado existe porque comparar "con publicaciones" contra "todos" es
	 * justo lo que se hace mirando una lista filtrada, y obligar a volver al panel
	 * para reponerlo convertía una comparación en cuatro clics. La aspa sí lo
	 * quita del todo.
	 */
	const [contentFilters, setContentFilters] = useState<Partial<Record<ContentFilterKey, boolean>>>({});
	/** La fila de filtros, desplegada. Es lo que abre el botón "Filtros" en
	 *  escritorio; en pantallas angostas ese botón abre el panel lateral. */
	const [filterRowOpen, setFilterRowOpen] = useState(false);
	const [sortField, setSortField] = useState<SortField>('nombre');
	const [sortDir, setSortDir] = useState<SortDirection>('asc');
	const [pageSize, setPageSize] = useState<PageSize>(25);
	const [page, setPage] = useState(1);
	// Ver el comentario del primer lote, más abajo: separa el render del servidor
	// del que toma el virtualizador.
	const [hydrated, setHydrated] = useState(false);
	useEffect(() => setHydrated(true), []);

	// El orden de llegada ES el orden por apellido: lo resuelve la API. El front no
	// puede recalcularlo — `formatoNombre` varía por persona y las partes del nombre
	// no viajan en el payload del directorio, así que "la última palabra del nombre
	// visible" sería una adivinanza. Se guarda la posición original para poder
	// volver a ese orden después de haber ordenado por otra cosa.
	const originalIndex = useMemo(() => {
		const map = new Map<string, number>();
		investigators.forEach((inv, index) => map.set(inv.slug, index));
		return map;
	}, [investigators]);

	/** Nombre completo → abreviatura, para las opciones de los filtros: en una caja
	 *  de 15rem no entra "Centro Regional de Investigación y Desarrollo Sustentable
	 *  de Atacama" y sí entra CRIDESAT. Las dos abreviaturas salen de la base
	 *  (`nombre_abreviado`), no de recortar el nombre. */
	const unitLabelByName = useMemo(() => {
		const map = new Map<string, string>();
		investigators.forEach((inv) => {
			if (inv.unit && !map.has(inv.unit)) map.set(inv.unit, nombreUnidad(inv));
		});
		return map;
	}, [investigators]);

	const facultyLabelByName = useMemo(() => {
		const map = new Map<string, string>();
		investigators.forEach((inv) => {
			if (inv.macroUnit && !map.has(inv.macroUnit)) {
				map.set(inv.macroUnit, nombreMacrounidad(inv));
			}
		});
		return map;
	}, [investigators]);

	const lineNameBySlug = useMemo(() => {
		const map = new Map<string, string>();
		researchLines.forEach((line) => map.set(line.slug, line.name));
		return map;
	}, [researchLines]);

	const lineBySlug = useMemo(
		() => new Map(researchLines.map((line) => [line.slug, line] as const)),
		[researchLines],
	);

	const faculties = useMemo(
		() =>
			Array.from(new Set(investigators.map((i) => i.macroUnit).filter(Boolean) as string[])).sort(
				(a, b) => a.localeCompare(b, 'es'),
			),
		[investigators],
	);

	// Las unidades dependen de la facultad elegida: con 28 unidades planas, elegir
	// facultad primero deja una lista de tres o cuatro en vez de la lista entera.
	const units = useMemo(() => unidadesDeFacultad(investigators, facultyFilter), [investigators, facultyFilter]);

	// Cascada Facultad -> Unidad -> líneas resuelta en UN paso (directoryScope): las líneas se juzgan contra
	// la facultad y la unidad SIGUIENTES, no contra una unidad que el mismo cambio está por resetear.
	const cascada = useMemo(
		() =>
			resolverCascada(investigators, lineBySlug, {
				facultad: facultyFilter,
				unidad: unitFilter,
				linea: lineFilter,
			}),
		[investigators, lineBySlug, facultyFilter, unitFilter, lineFilter],
	);

	// Las líneas UDA se acotan igual que las unidades, pero con Facultad Y Unidad: son las que tienen
	// las personas que hoy cumplen esos dos filtros (por su unidad principal). Con la lista global,
	// elegir "Enfermería" seguía ofreciendo líneas que nadie de Enfermería tiene. La búsqueda y el
	// estamento NO las acotan, igual que no acotan a las unidades. Las líneas personalizadas NO entran
	// aquí: son texto libre sin ninguna unidad; su lista sigue a los RESULTADOS, ver `candidatos`.
	const scopedInvestigators = useMemo(
		() => investigadoresEnAlcance(investigators, facultyFilter, cascada.unidad),
		[investigators, facultyFilter, cascada.unidad],
	);

	const lineOptions = useMemo(
		() => opcionesDeLinea(scopedInvestigators, lineBySlug),
		[scopedInvestigators, lineBySlug],
	);

	// Mismo orden que usa el criterio "estamento" al ordenar: si el desplegable
	// los listara alfabéticamente y la lista saliera jerárquica, cada uno diría
	// una cosa distinta sobre lo mismo.
	const estamentos = useMemo(() => {
		const bySlug = new Map<string, string | null | undefined>();
		investigators.forEach((i) => {
			if (i.rolInvestigadorNombre && !bySlug.has(i.rolInvestigadorNombre)) {
				bySlug.set(i.rolInvestigadorNombre, i.tipoInvestigador);
			}
		});
		return Array.from(bySlug.keys()).sort(
			(a, b) =>
				estamentoRank(bySlug.get(a)) - estamentoRank(bySlug.get(b)) || a.localeCompare(b, 'es'),
		);
	}, [investigators]);

	// Cambiar de facultad puede dejar seleccionada una unidad que ya no pertenece a ella, y cambiar
	// facultad o unidad puede dejar una línea UDA que ya no se ofrece: el listado quedaría vacío sin que
	// se vea por qué. La regla vive en `resolverCascada`; aquí sólo se aplica, un campo a la vez. El filtro
	// de líneas personalizadas no se reinicia nunca por esto: ninguna unidad lo condiciona.
	useEffect(() => {
		if (cascada.unidad !== unitFilter) setUnitFilter(cascada.unidad);
		if (cascada.linea !== lineFilter) setLineFilter(cascada.linea);
	}, [cascada, unitFilter, lineFilter]);

	// Qué filtros de contenido tienen a alguien que ofrecer con los datos de hoy, y
	// están permitidos para quien mira.
	const isAdmin = user?.role === 'admin';
	const availableContentFilters = useMemo(
		() =>
			CONTENT_FILTERS.filter(
				(filter) =>
					(!('adminOnly' in filter) || !filter.adminOnly || isAdmin) &&
					investigators.some(filter.has),
			),
		[investigators, isAdmin],
	);
	/** La tercera fila de la barra (interruptores de gestión) sólo existe para admin y si hay alguno. */
	const showAdminChips = isAdmin && availableContentFilters.length > 0;

	/** Encendidos: los únicos que recortan el listado. */
	const activeContentFilters = useMemo(
		() => availableContentFilters.filter((filter) => contentFilters[filter.key] === true),
		[availableContentFilters, contentFilters],
	);

	const hasPublicationData = useMemo(
		() => investigators.some((i) => (i.publicacionesCount ?? 0) > 0),
		[investigators],
	);

	/** Pone o enciende un filtro. */
	const setContentFilter = (key: ContentFilterKey, value: boolean) => {
		setContentFilters((current) => ({ ...current, [key]: value }));
		setPage(1);
	};

	/** Lo retira de la fila por completo. */
	const removeContentFilter = (key: ContentFilterKey) => {
		setContentFilters((current) => {
			const next = { ...current };
			delete next[key];
			return next;
		});
		setPage(1);
	};

	const sortFields = useMemo(
		() =>
			SORT_FIELDS.filter(
				(field) => hasPublicationData || !SORT_FIELDS_NEED_PUBLICATIONS.includes(field.value),
			),
		[hasPublicationData],
	);

	// El criterio de orden elegido puede desaparecer del selector (los que dependen
	// de publicaciones sólo se ofrecen si hay datos): sin esto quedaría un `select`
	// sin opción que lo respalde.
	useEffect(() => {
		if (!sortFields.some((field) => field.value === sortField)) {
			setSortField('nombre');
		}
	}, [sortFields, sortField]);

	/**
	 * Quiénes pasan la búsqueda y TODOS los filtros salvo el de líneas personalizadas (con su puntaje de
	 * búsqueda, que sirve para ordenar). Es el universo del que sale la lista del filtro "Línea": una línea
	 * personalizada no pertenece a ninguna unidad, pero sus opciones tienen que acompañar lo que se ve —si
	 * escribes "cic" o eliges la Facultad CIC, sólo ofrece las de esas personas—. Excluye al propio filtro, como
	 * cualquier faceta, para que la elegida siga listada junto a sus alternativas; sus resultados salen de
	 * acá mismo en `filteredInvestigators`.
	 */
	const candidatos = useMemo(() => {
		const hasQuery = search.trim().length > 0;
		const filtros = {
			facultad: facultyFilter,
			unidad: unitFilter,
			linea: lineFilter,
			estamento: estamentoFilter,
		};

		return investigators.flatMap((inv) => {
			// Dos grupos, y la diferencia no es cosmética. El nombre admite el tramo
			// difuso del scorer (subsecuencia): escribir "mrco abrca" tiene que
			// encontrar a Marco Abarca. Los campos descriptivos NO lo admiten —
			// contra textos largos la subsecuencia matchea casi cualquier consulta
			// corta y el buscador deja de discriminar.
			const nameScore = scoreNormalizedFuzzySearch(search, [inv.fullName, inv.displayName]);
			const fieldScore = scoreNormalizedFuzzySearch(
				search,
				[
					inv.orcid,
					inv.unit,
					inv.unitShort ?? '',
					inv.macroUnit ?? '',
					inv.rolInvestigadorNombre ?? '',
					...textosDeLineasParaBuscar(inv, lineNameBySlug),
				],
				false,
			);
			const score = Math.max(nameScore, fieldScore);
			const pasa =
				(!hasQuery || score > 0) &&
				coincideConFiltros(inv, filtros) &&
				activeContentFilters.every((filter) => filter.has(inv));
			return pasa ? [{ inv, score }] : [];
		});
	}, [
		investigators,
		search,
		facultyFilter,
		unitFilter,
		lineFilter,
		estamentoFilter,
		activeContentFilters,
		lineNameBySlug,
	]);

	// Distintas por texto sin mayúsculas ni tildes, de las personas de `candidatos`. La elegida nunca se cae
	// de la lista, aunque los demás filtros ya no la ofrezcan: ninguno la reinicia.
	const customLineOptions = useMemo(
		() =>
			opcionesDeLineaPersonalizada(
				candidatos.map(({ inv }) => inv),
				customLineFilter,
				investigators,
			),
		[candidatos, customLineFilter, investigators],
	);

	/**
	 * Los filtros de selección, declarados una vez y renderizados en DOS lugares: el
	 * panel y la fila de filtros aplicados. Con el markup duplicado, agregar un
	 * filtro obligaba a acordarse de los dos sitios y a mantenerlos sincronizados.
	 */
	const lineMeta = new Map(lineOptions.map((o) => [o.value, o] as const));
	// Las dos listas de líneas llevan nombres largos: una opción es UNA línea con puntos suspensivos y el
	// texto completo en `title`. La unidad dueña va aparte y atenuada, y entra en lo que busca el menú.
	const renderLineOption = (option: SelectOption): ReactNode => {
		const meta = lineMeta.get(option.value);
		return <LineOptionLabel label={option.label} unit={meta?.unit ?? ''} title={meta?.title ?? option.label} />;
	};
	const renderCustomLineOption = (option: SelectOption): ReactNode => (
		<LineOptionLabel label={option.label} unit="" title={option.label} />
	);
	const lineSearchText = (option: SelectOption) => `${option.label} ${lineMeta.get(option.value)?.unit ?? ''}`;
	const scopeIsEmpty = facultyFilter === 'all' && unitFilter === 'all';
	// La lista de líneas personalizadas también se acota con la búsqueda, el estamento y los filtros de contenido.
	const resultsAreNarrowed =
		!scopeIsEmpty ||
		search.trim() !== '' ||
		lineFilter !== 'all' ||
		estamentoFilter !== 'all' ||
		activeContentFilters.length > 0;

	const selectFilters: SelectFilterDef[] = [
		{
			key: 'facultad',
			Icon: Landmark,
			label: 'Facultad',
			allLabel: 'Todas',
			value: facultyFilter,
			onChange: withPageReset(setFacultyFilter),
			options: faculties.map((v) => ({ value: v, label: facultyLabelByName.get(v) ?? v })),
			hint: undefined,
			emptyHint: undefined,
		},
		{
			key: 'unidad',
			Icon: Building2,
			label: 'Unidad',
			allLabel: 'Todas',
			value: unitFilter,
			onChange: withPageReset(setUnitFilter),
			options: units.map((v) => ({ value: v, label: unitLabelByName.get(v) ?? v })),
			hint: facultyFilter === 'all' ? undefined : `${units.length} en ${facultyFilter}`,
			emptyHint: undefined,
		},
		{
			key: 'linea',
			Icon: GraduationCap,
			label: 'Línea UDA',
			allLabel: 'Todas',
			value: lineFilter,
			onChange: withPageReset(setLineFilter),
			options: lineOptions.map((o) => ({ value: o.value, label: o.label })),
			hint: leyendaDeAlcance(facultyFilter, unitFilter, lineOptions.length),
			// Sin opciones, el selector explica por qué está vacío en vez de parecer roto.
			emptyHint: scopeIsEmpty
				? 'Ningún perfil tiene líneas cargadas todavía'
				: 'Ningún perfil de esta selección tiene líneas',
			renderOption: renderLineOption,
			getOptionSearchText: lineSearchText,
			menuMinWidth: LINE_MENU_MIN_WIDTH,
		},
		{
			key: 'lineasPersonalizadas',
			Icon: PenLine,
			// Corto a propósito: el nombre se oculta en cuanto hay un valor, y entre "Línea UDA" (catálogo
			// institucional, icono de birrete) y "Otra línea" (texto libre, icono de lápiz) el icono ya los distingue.
			// Sus opciones siguen a los resultados: las líneas personalizadas de quienes pasan la búsqueda y los demás
			// filtros (ver `candidatos`).
			label: 'Otra línea',
			allLabel: 'Todas',
			value: customLineFilter,
			onChange: withPageReset(setCustomLineFilter),
			options: customLineOptions,
			// Sin leyenda "N en ...": una línea personalizada no pertenece a ninguna unidad.
			hint: undefined,
			emptyHint: resultsAreNarrowed
				? 'Ningún perfil de esta selección tiene líneas personalizadas'
				: 'Ningún perfil tiene líneas personalizadas todavía',
			renderOption: renderCustomLineOption,
			menuMinWidth: LINE_MENU_MIN_WIDTH,
		},
		{
			key: 'estamento',
			Icon: Award,
			label: 'Estamento',
			allLabel: 'Todos',
			value: estamentoFilter,
			onChange: withPageReset(setEstamentoFilter),
			options: estamentos.map((v) => ({ value: v, label: v })),
			hint: undefined,
			emptyHint: undefined,
		},
	];

	/**
	 * El aspa DEVUELVE EL FILTRO A "todas"; no lo retira de la fila.
	 *
	 * Antes hacía las dos cosas —vaciar el valor y sacar el control— y ésa era la
	 * confusión: el mismo gesto que usás para corregir una elección te dejaba sin
	 * el filtro, y para recuperarlo había que saber que existía un `+` en la punta
	 * de la fila. Los cinco filtros son fijos, así que el aspa sólo tiene un
	 * trabajo posible y lo hace siempre.
	 */
	function clearSelectFilter(filter: SelectFilterDef) {
		filter.onChange('all');
	}

	const activeFilterCount =
		(facultyFilter === 'all' ? 0 : 1) +
		(unitFilter === 'all' ? 0 : 1) +
		(lineFilter === 'all' ? 0 : 1) +
		(customLineFilter === 'all' ? 0 : 1) +
		(estamentoFilter === 'all' ? 0 : 1) +
		activeContentFilters.length;

	function resetFilters() {
		setFacultyFilter('all');
		setUnitFilter('all');
		setLineFilter('all');
		setCustomLineFilter('all');
		setEstamentoFilter('all');
		setContentFilters({});
		setPage(1);
	}

	/** Cualquier cambio de filtro invalida la página en que estabas. */
	function withPageReset<T>(setter: (value: T) => void) {
		return (value: T) => {
			setter(value);
			setPage(1);
		};
	}

	const filteredInvestigators = useMemo(() => {
		const hasQuery = search.trim().length > 0;
		const scored = candidatos.filter(({ inv }) => tieneLineaPersonalizada(inv, customLineFilter));

		// Con búsqueda manda la relevancia, no el orden elegido: quien literalmente se
		// llama como el término va arriba. El orden del selector vuelve a mandar en
		// cuanto se vacía la búsqueda.
		if (hasQuery) {
			scored.sort(
				(a, b) => b.score - a.score || a.inv.displayName.localeCompare(b.inv.displayName, 'es'),
			);
			return scored.map(({ inv }) => inv);
		}

		const sign = sortDir === 'asc' ? 1 : -1;
		const compare = (a: InvestigatorRecord, b: InvestigatorRecord) => {
			switch (sortField) {
				case 'nombre':
					return a.displayName.localeCompare(b.displayName, 'es');
				case 'unidad':
					return (a.unit || '').localeCompare(b.unit || '', 'es');
				case 'facultad':
					return (a.macroUnit || '').localeCompare(b.macroUnit || '', 'es');
				case 'estamento': {
					const rank = estamentoRank(a.tipoInvestigador) - estamentoRank(b.tipoInvestigador);
					// Empatan los dos que comparten estamento y los dos que no están en la
					// lista: ahí el nombre visible los ordena entre sí.
					return (
						rank ||
						(a.rolInvestigadorNombre || '').localeCompare(b.rolInvestigadorNombre || '', 'es')
					);
				}
				// Los numéricos se comparan al revés que los textos: "ascendente" en un
				// contador de publicaciones se lee como "de más a menos", no al revés.
				case 'publicaciones':
					return (b.publicacionesCount ?? 0) - (a.publicacionesCount ?? 0);
				case 'ultimaPublicacion':
					return (b.ultimoAnioPublicacion ?? 0) - (a.ultimoAnioPublicacion ?? 0);
				case 'apellido':
				default:
					return (originalIndex.get(a.slug) ?? 0) - (originalIndex.get(b.slug) ?? 0);
			}
		};

		return scored
			.map(({ inv }) => inv)
			.sort((a, b) => {
				const primary = compare(a, b) * sign;
				// Desempate estable: dos personas de la misma unidad tienen que salir
				// siempre en el mismo orden, no en el que quedó de la pasada anterior.
				if (primary !== 0) return primary;
				return (originalIndex.get(a.slug) ?? 0) - (originalIndex.get(b.slug) ?? 0);
			});
	}, [candidatos, search, customLineFilter, sortField, sortDir, originalIndex]);

	const totalItems = filteredInvestigators.length;
	const totalPages = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
	const safePage = Math.min(page, Math.max(1, totalPages));

	const visibleInvestigators =
		pageSize === 0
			? filteredInvestigators
			: filteredInvestigators.slice((safePage - 1) * pageSize, safePage * pageSize);

	const rangeStart = pageSize === 0 ? 1 : (safePage - 1) * pageSize + 1;
	const rangeEnd = pageSize === 0 ? totalItems : Math.min(safePage * pageSize, totalItems);

	// Ancho, alto y tipografía de la tarjeta salen todos de acá, y `estimateSize`
	// consume el MISMO objeto: no hay forma de mover una medida y olvidar la otra.
	/**
	 * En un teléfono el tamaño de tarjeta no se elige: es siempre el 100 %.
	 *
	 * A ese ancho entra UNA tarjeta por fila, así que agrandarlas o achicarlas no
	 * cambia cuántas ves — sólo cuánto scrolleás. El control ocupaba media barra
	 * de pie para no decidir nada. Se deriva en vez de escribir el estado: al
	 * volver a una pantalla ancha, el zoom que habías elegido sigue ahí.
	 */
	const effectiveZoom = isMobile ? ZOOM_DEFAULT : zoom;
	const card = getDirectoryCardMetrics(effectiveZoom);

	// ── Virtualización (R46) ───────────────────────────────────────────────
	// Con "Todos" seleccionado se listan 700+ investigadores. Montar todos los
	// nodos congela la vista. La virtualización mantiene en el DOM solo las filas
	// visibles (+ un pequeño overscan), así el scroll fluye con miles de ítems.
	// El contenedor scrolleable compartido es el scroll element de ambos virtualizers.
	const scrollRef = useRef<HTMLDivElement>(null);
	const [viewportWidth, setViewportWidth] = useState(0);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const measure = () => setViewportWidth(el.clientWidth);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const GRID_GAP = 16; // gap-4
	const SCROLL_PADDING = 20; // padding del contenedor (1.25rem por lado)
	// Cuántas tarjetas entran por fila con el ancho actual (replica el cálculo de
	// `repeat(auto-fill, minmax(card.width, 1fr))`), para agrupar en filas virtuales.
	const columnCount = useMemo(() => {
		const usable = Math.max(0, viewportWidth - SCROLL_PADDING * 2);
		if (usable === 0) return 1;
		return Math.max(1, Math.floor((usable + GRID_GAP) / (card.width + GRID_GAP)));
	}, [viewportWidth, card.width]);

	const gridRowCount = Math.ceil(visibleInvestigators.length / columnCount);

	// Grid: virtualiza FILAS (cada fila renderiza `columnCount` tarjetas).
	const gridVirtualizer = useVirtualizer({
		count: view === 'grid' ? gridRowCount : 0,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => card.height + GRID_GAP,
		overscan: 3,
	});

	// Lista compacta: una fila por investigador (altura uniforme).
	const listVirtualizer = useVirtualizer({
		count: view === 'compact' ? visibleInvestigators.length : 0,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 84, // ~72px de tarjeta + 12px de separación
		overscan: 8,
	});

	// Tabla: se renderiza como div-grid con COLUMNAS FIJAS (no <table>), porque el
	// `table-layout:auto` recalcula anchos según las filas montadas → al virtualizar,
	// las columnas se "corren". Con columnas fijas los datos quedan alineados aunque
	// solo esté montado un subconjunto. El header va sticky (fuera del área virtual);
	// se mide el offset del cuerpo para pasarlo como `scrollMargin`.
	// El RUT ERA una columna, y se retiró: es dato personal y no viaja —ni debe
	// viajar— en un payload público anónimo de 270 personas, así que la columna
	// mostraba una cadena vacía en todas las filas. La reemplaza la facultad, que
	// sí llega y ordena de verdad el directorio.
	const TABLE_COLUMNS =
		'minmax(0, 2.2fr) minmax(0, 1.8fr) minmax(0, 1.6fr) minmax(0, 1.4fr) 120px';
	const tableBodyRef = useRef<HTMLDivElement>(null);
	const [tableScrollMargin, setTableScrollMargin] = useState(0);
	useEffect(() => {
		if (view !== 'table') return;
		const scroll = scrollRef.current;
		const body = tableBodyRef.current;
		if (!scroll || !body) return;
		const measure = () => {
			const bRect = body.getBoundingClientRect();
			const sRect = scroll.getBoundingClientRect();
			setTableScrollMargin(bRect.top - sRect.top + scroll.scrollTop);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(scroll);
		return () => observer.disconnect();
	}, [view]);

	const tableVirtualizer = useVirtualizer({
		count: view === 'table' ? visibleInvestigators.length : 0,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 45, // fila px-4 py-3 (~44px)
		overscan: 10,
		scrollMargin: tableScrollMargin,
	});

	// Si la ventana se angosta estando en tabla, la vista se queda sin su botón y
	// sin forma de salir: se vuelve a la grilla.
	useEffect(() => {
		if (isMobile && view === 'table') setView('grid');
	}, [isMobile, view]);

	// La altura de tarjeta (zoom) y el nº de columnas cambian el layout → remedir.
	useEffect(() => {
		gridVirtualizer.measure();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [card.height, columnCount, view]);

	function handlePageSizeChange(value: number) {
		setPageSize(value as PageSize);
		setPage(1);
	}

	// "Ir a mi perfil": acción propia del directorio (no del shell global).
	// Solo con sesión y perfil creado; se inyecta en el topbar vía portal, mismo
	// estilo que "Exportar CV" (btnSecondary).
	const showMyProfileAction =
		status === 'authenticated' && user?.idInvestigador != null;

	return (
		<>
			{portalTarget && showMyProfileAction
				? createPortal(
						<Link
							href={myProfileHref(user)}
							className={sidebarStyles.btnSecondary}
							aria-label="Ir a mi perfil"
						>
							<UserRound size={13} />
							Ir a mi perfil
						</Link>,
						portalTarget,
					)
				: null}

			{/* ── Panel de filtros, flotando sobre la vista ── */}
			{panelTarget
				? createPortal(
						<div className="flex flex-col gap-3.5">
							{selectFilters.map((filter) => (
								<PanelSelect
									key={filter.key}
									label={filter.label}
									Icon={filter.Icon}
									value={filter.value}
									onChange={filter.onChange}
									options={filter.options}
									allLabel={filter.allLabel}
									hint={filter.hint}
									emptyHint={filter.emptyHint}
									renderOption={filter.renderOption}
									getOptionSearchText={filter.getOptionSearchText}
									menuMinWidth={filter.menuMinWidth}
								/>
							))}

							{availableContentFilters.map((filter) => (
								<PanelToggle
									key={filter.key}
									label={filter.label}
									Icon={filter.Icon}
									checked={contentFilters[filter.key] === true}
									onChange={(checked) =>
										checked ? setContentFilter(filter.key, true) : removeContentFilter(filter.key)
									}
								/>
							))}

							{activeFilterCount > 0 ? (
								<button
									type="button"
									onClick={resetFilters}
									className="cursor-pointer rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
								>
									Limpiar {activeFilterCount} {activeFilterCount === 1 ? 'filtro' : 'filtros'}
								</button>
							) : null}
						</div>,
						panelTarget,
					)
				: null}

			<section
				data-fills-viewport
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: '1 1 0',
					minHeight: 0,
					overflow: 'hidden',
					borderRadius: '10px',
					border: '1px solid #e2e8f0',
					backgroundColor: '#ffffff',
					boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
				}}
			>
			{/* ── Toolbar: sólo lo que se usa en cada consulta ──
			    Los filtros se fueron al carril derecho. Acá quedan la búsqueda —que
			    tiene que estar SIEMPRE a la vista— el orden y el cambio de vista. En
			    móvil la barra pasaba de ~250 px de alto a esto. */}
			{/* El padding derecho suma el ancho de la barra de scroll, que sólo existe
			    en el área de tarjetas: con los tres bloques a `1.25rem` planos, la barra
			    y el pie llegaban unos pixeles más a la derecha que las tarjetas. Es el
			    mismo ajuste que alinea el topbar con el contenido, una capa más adentro. */}
			<div
				style={{
					flexShrink: 0,
					borderBottom: '1px solid #f1f5f9',
					// 1rem arriba y abajo contra 1.25rem a los lados. El área de tarjetas
					// respira 1.25rem por los cuatro costados, y la barra traía 0.75rem:
					// contra el mismo borde, dos huecos tan distintos se leen como un error
					// de maquetación. No se igualan del todo a propósito — el alto es el
					// recurso escaso de esta vista, que no scrollea como documento, y la
					// línea divisoria ya separa por su cuenta. La proporción 16:20 se lee
					// pareja; 12:20 no.
					padding: '1rem 1.25rem',
					paddingRight: 'calc(1.25rem + var(--siiv-scrollbar, 0px))',
				}}
			>
				<div className="flex w-full flex-wrap items-center gap-2">
					{/* El buscador se queda con el ancho que sobra, así la fila llega de
					    borde a borde igual que las tarjetas de abajo y el grupo de vistas
					    apoya contra el filo derecho.
					    `select-none` en la caja: arrastrando el mouse desde fuera hacia la
					    barra se seleccionaba el texto del marcador de posición, que no es
					    contenido y no debería poder copiarse. */}
					{/* `w-full` en el teléfono y no `flex-1`: con `flex-wrap`, un elemento
					    flexible sólo se estira dentro de la línea que le tocó, así que si
					    algo más entraba a su lado el campo quedaba corto y la barra no
					    llegaba al filo de las tarjetas. Ocupando la fila entera, cada bloque
					    de la barra empieza y termina donde empiezan y terminan las tarjetas
					    de abajo. */}
					<label className={`${CONTROL_CLASS} flex w-full min-w-[180px] select-none items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-3 sm:w-auto sm:flex-1`}>
						<Search className="h-4 w-4 shrink-0 text-slate-500" />
						<input
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(1);
							}}
							// El listado completo no entra en 390 px: se corta donde deja de
							// caber en vez de recortarse solo, que dejaba "Nombre, ORCID, unidad,"
							// con la coma colgando. Los campos que no se nombran se buscan igual.
							placeholder={isMobile ? 'Nombre, ORCID, unidad…' : 'Nombre, ORCID, unidad, facultad o línea'}
							aria-label="Buscar investigadores"
							// `:placeholder-shown` y no `select-none` a secas: con el campo vacío
							// no hay nada que copiar y el marcador de posición no debería poder
							// seleccionarse; en cuanto se escribe algo, ese texto sí se
							// selecciona como en cualquier campo.
							className="w-full border-0 bg-transparent p-0 text-sm text-slate-700 outline-none [&:placeholder-shown]:select-none"
						/>
						{/* El resultado, dentro del propio campo.
						    Es el único sitio que no le quita espacio a nada: usa el aire que
						    el campo ya desperdicia a la derecha. Y está pegado a la búsqueda,
						    que es lo que más lo cambia. Antes pasó por el pie, por el final
						    de la fila de filtros, por el arranque de la barra y por el
						    breadcrumb; las cuatro veces ocupaba un lugar que le disputaba
						    espacio a algún control.
						    No desaparece al escribir —es justo cuando más se mueve— y con
						    filtros muestra "12/269", porque el total es lo que da la medida
						    de cuánto se recortó. */}
						{/* `key={totalItems}` remonta el nodo cada vez que el número cambia, y
						    eso vuelve a disparar la animación — con una clase habría que
						    agregarla y quitarla a mano con un temporizador. El pulso existe
						    porque el dato se mueve mientras el ojo está en el campo o en un
						    filtro: sin él, el cambio pasa desapercibido. */}
						{/* En el teléfono se lee "54/269" y no "54 resultados", y no es una
						    contradicción de lo anterior: el total del pie se oculta en
						    pantallas angostas, así que sin la fracción el número de arriba se
						    queda sin referencia — 54 no dice nada si no sabés contra cuánto.
						    Donde el pie SÍ muestra el total, la fracción sobra. */}
						<span
							key={totalItems}
							className="siiv-count-pulse shrink-0 whitespace-nowrap text-xs font-semibold text-[#233a4d]"
							title={`${totalItems} de ${investigators.length} investigadores`}
							aria-live="polite"
						>
							<span className="tabular-nums">{totalItems}</span>
							<span className="tabular-nums sm:hidden">/{investigators.length}</span>
							<span className="hidden sm:inline">
								{' '}
								{totalItems === 1 ? 'resultado' : 'resultados'}
							</span>
						</span>
					</label>

					{/* Orden y dirección como UN control, no como dos pegados: son la misma
					    decisión —"por qué campo y hacia dónde"— y separarlos en dos cajas
					    con su propio borde las hacía leer como opciones sin relación.
					    El desplegable es el del sistema (`SelectInput`), el mismo de los
					    modales de edición: el nativo abre el menú del sistema operativo,
					    que no se parece a nada del resto de la aplicación. */}
					<div
						className={`${CONTROL_CLASS} flex min-w-0 flex-1 items-center overflow-hidden rounded-[10px] border border-slate-200 bg-white sm:flex-none sm:shrink-0`}
					>
						{/* El disparador dice "Ordenar" antes del campo, y lleva icono como el
						    resto de la barra.
						    Sin eso, un desplegable que pone "Nombre" pegado al buscador se
						    lee como el criterio de BÚSQUEDA —"busco por nombre"— cuando es el
						    de ORDEN. El menú sigue mostrando los campos a secas: ahí el
						    contexto ya lo da el propio menú. Se descartó poner una etiqueta
						    encima: duplicaba el alto de la barra para resolver un control de
						    seis, y los otros cinco ya dicen en su texto lo que son. */}
						<div className="h-full min-w-0 flex-1 sm:w-[186px] sm:flex-none">
							<SelectInput
								value={sortField}
								onChange={(value) => withPageReset(setSortField)(value as SortField)}
								options={sortFields.map((field) => ({ value: field.value, label: field.label }))}
								className="!h-full !min-w-0 !rounded-none !border-0 !bg-transparent !text-sm hover:!bg-slate-50"
								renderTrigger={({ selected }) => (
									<>
										<span className="flex min-w-0 items-center gap-1.5">
											<ArrowUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
											{/* La palabra se va en el teléfono: el icono de las dos flechas ya
											    dice qué es, y ahí el control está en su propia fila, lejos del
											    buscador. Desde `sm` vuelve, porque pegado al campo de búsqueda
											    un desplegable que pone "Nombre" se lee como el criterio de
											    BÚSQUEDA y no como el de ORDEN. */}
											<span className="hidden shrink-0 text-xs text-slate-500 sm:inline">Ordenar</span>
											<span className="truncate text-sm font-medium text-slate-700">
												{selected?.label}
											</span>
										</span>
										<ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
									</>
								)}
							/>
						</div>
						<span className="h-full w-px shrink-0 bg-slate-200" aria-hidden="true" />
						<button
							type="button"
							onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
							aria-label={sortDir === 'asc' ? 'Orden ascendente' : 'Orden descendente'}
							title={sortDir === 'asc' ? 'Ascendente' : 'Descendente'}
							className="inline-flex h-full w-9 shrink-0 cursor-pointer items-center justify-center text-slate-600 transition hover:bg-slate-50"
						>
							{sortDir === 'asc' ? (
								<ArrowUpNarrowWide className="h-4 w-4" />
							) : (
								<ArrowDownWideNarrow className="h-4 w-4" />
							)}
						</button>
					</div>

					{/* El botón de filtros, en dos versiones que NO son la misma cosa con
					    otro tamaño: en escritorio despliega la fila de filtros, y en
					    pantallas angostas abre el panel lateral.

					    Los filtros vivían a la vez en la fila y en el panel, y tener el
					    mismo control en dos sitios obligaba a mantener los dos y a decidir
					    en cuál mirar. Ahora cada ancho usa el que le sirve: en escritorio
					    sobra ancho horizontal y la fila deja los filtros a la vista sin
					    tapar nada; en un teléfono no hay fila que alcance, y el panel —que
					    ya estaba hecho y se conserva entero— es la única forma. */}
					<button
						type="button"
						onClick={() => setFilterRowOpen((open) => !open)}
						aria-expanded={filterRowOpen}
						className={`${CONTROL_CLASS} hidden cursor-pointer items-center gap-1.5 rounded-[10px] border px-3 text-xs font-semibold transition lg:inline-flex ${
							filterRowOpen || activeFilterCount > 0
								? 'border-[#233a4d] bg-[#233a4d] text-white'
								: 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
						}`}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						Filtros
						{activeFilterCount > 0 ? (
							<span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white/20 px-1 text-[10px] font-semibold text-white">
								{activeFilterCount}
							</span>
						) : null}
					</button>

					{/* En el teléfono, cuadrado y sólo con el icono: la palabra "Filtros"
					    son los ~50 px que necesita el desplegable de orden para mostrar su
					    etiqueta y el campo elegido sin recortarlos. El contador pasa a la
					    esquina —superpuesto, no en línea— para que el botón siga cuadrado
					    con filtros puestos. */}
					<button
						type="button"
						onClick={sidePanel.toggle}
						aria-expanded={sidePanel.open}
						aria-label="Filtros"
						className={`${CONTROL_CLASS} relative inline-flex w-[var(--siiv-control-height,36px)] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border px-0 text-xs font-semibold transition sm:w-auto sm:px-3 lg:hidden ${
							sidePanel.open || activeFilterCount > 0
								? 'border-[#233a4d] bg-[#233a4d] text-white'
								: 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
						}`}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						<span className="hidden sm:inline">Filtros</span>
						{activeFilterCount > 0 ? (
							<span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-[#233a4d] px-1 text-[10px] font-semibold text-white sm:static sm:h-4 sm:border-0 sm:bg-white/20">
								{activeFilterCount}
							</span>
						) : null}
					</button>

					{/* Separador: a la izquierda queda lo que decide QUÉ se ve —búsqueda,
					    orden, filtros—; a la derecha, CÓMO se ve. Sin él los seis controles
					    se leen como una sola lista de botones sin jerarquía. */}
					{/* Margen más ajustado en el teléfono: los 16 px de `mx-2` son ancho que
					    ahí necesitan el orden y los botones, y la línea separa igual con la
					    mitad. */}
					<span className="mx-1 h-5 w-px shrink-0 bg-slate-200 sm:mx-2" aria-hidden="true" />

					{/* La TABLA no se ofrece en el teléfono: son cuatro columnas de datos
					    en 390 px, y cualquier reparto deja los nombres de unidad recortados
					    a dos palabras. Quedan la grilla y la lista, que son las dos que
					    funcionan en una columna. Si se llegó en tabla y la ventana se
					    angosta, el efecto de abajo devuelve la vista a la grilla. */}
					<div className="flex shrink-0 items-center gap-2 sm:ml-auto">
						<ViewButton active={view === 'grid'}    label="Grid"  onClick={() => setView('grid')}    Icon={LayoutGrid} />
						<span className="hidden sm:inline-flex">
							<ViewButton active={view === 'table'} label="Tabla" onClick={() => setView('table')} Icon={TableProperties} />
						</span>
						<ViewButton active={view === 'compact'} label="Lista" onClick={() => setView('compact')} Icon={List} />
					</div>
				</div>

				{/* ── Segunda fila: los cinco filtros del directorio ──
				    No son etiquetas de sólo lectura: cada uno trae su propio desplegable,
				    así que corregir "Facultad de Ingeniería" por otra se hace en el sitio.
				    Son FIJOS —siempre estos cinco, siempre a la vista— y el aspa devuelve
				    el filtro a "todas" en vez de retirarlo.

				    Antes se podían agregar y quitar, con un `+` al final de la fila. Se
				    retiró porque el aspa terminaba haciendo dos cosas a la vez: el mismo
				    gesto con que corregís una elección te dejaba sin el control, y
				    recuperarlo exigía descubrir el `+`. Con cinco filtros fijos no hay
				    nada que descubrir ni que reponer. (El historial guarda cómo estaba:
				    `acba890`.)

				    Se REPARTEN EL ANCHO por igual, todas las columnas del mismo ancho, en vez
				    de medir 15rem cada una: la fila llega de borde a borde igual que las
				    tarjetas de abajo, y los nombres largos de unidad entran sin recortarse.
				    La grilla les fija el ancho, así que elegir un valor largo no recompone
				    la fila — que era la razón por la que estaba fijado a mano. Al final de
				    la MISMA fila va "Limpiar", con su hueco siempre reservado.

				    La fila vive SÓLO en escritorio (`lg:`): en pantallas angostas los
				    mismos filtros están en el panel lateral, y tenerlos en los dos sitios
				    era el problema que este rediseño vino a sacar. Se despliega con el
				    botón, y se queda desplegada mientras haya algún filtro con valor: que
				    se cierre sola escondería el motivo de que la lista esté recortada. */}
				{filterRowOpen || activeFilterCount > 0 ? (
					<div className="mt-2 hidden flex-col gap-1.5 lg:flex">
						{/* Los cinco selects se REPARTEN POR IGUAL el ancho que deja "Limpiar", que ocupa su
						    hueco SIEMPRE (invisible mientras no hay nada que limpiar): así la fila no se
						    recompone al elegir o quitar un filtro, y no hay segunda fila que haga saltar la
						    lista. Todas las columnas miden lo mismo: sin excepciones por etiqueta larga,
						    porque el nombre del filtro se oculta cuando hay valor y el valor entra entero. */}
						<div
							className="grid w-full min-w-0 items-center gap-1.5"
							style={{ gridTemplateColumns: `repeat(${selectFilters.length}, minmax(0, 1fr)) auto` }}
						>
							{selectFilters.map((filter) => (
								<ActiveSelectFilter
									key={filter.key}
									label={filter.label}
									Icon={filter.Icon}
									value={filter.value}
									allLabel={filter.allLabel}
									options={filter.options}
									hint={filter.options.length > 0 ? filter.hint : undefined}
									onChange={filter.onChange}
									onClear={() => clearSelectFilter(filter)}
									renderOption={filter.renderOption}
									getOptionSearchText={filter.getOptionSearchText}
									menuMinWidth={filter.menuMinWidth}
								/>
							))}
							{/* `invisible` (visibility: hidden) y no `hidden`: conserva el ancho, y de paso lo saca
							    del orden de tabulación y del árbol de accesibilidad mientras no hay nada que
							    limpiar. `disabled` por si algo lo alcanzara igual. Limpia TODO: selects y filtros
							    de gestión. */}
							<button
								type="button"
								onClick={resetFilters}
								disabled={activeFilterCount === 0}
								className={`${CONTROL_CLASS} inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[10px] px-2.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 ${
									activeFilterCount === 0 ? 'invisible' : ''
								}`}
							>
								<Eraser className="h-3.5 w-3.5" aria-hidden="true" />
								Limpiar
							</button>
						</div>

						{/* ── Fila de gestión (segunda de la grilla) ──
						    Los filtros de sí/no miran el estado de carga de cada ficha
						    (tiene foto, tiene biografía). A quien busca a una persona no le
						    sirven para encontrarla, y de paso exponen qué perfiles están a
						    medio completar; por eso son `adminOnly` y viven en su propia
						    línea, donde no se mezclan con los cinco de arriba.
						    Van todos a la vista y son interruptores: un clic enciende, otro
						    apaga. Sin aspa — con la lista completa siempre presente, retirar
						    uno no significaba nada distinto de apagarlo. */}
						{showAdminChips ? (
							<div className="flex flex-wrap items-center gap-1.5">
								{availableContentFilters.map((filter) => (
									<FilterChip
										key={filter.key}
										label={filter.chip}
										Icon={filter.Icon}
										enabled={contentFilters[filter.key] === true}
										onToggle={() => setContentFilter(filter.key, contentFilters[filter.key] !== true)}
									/>
								))}
							</div>
						) : null}
					</div>
				) : null}
			</div>

			{/* ── Scrollable content area ── */}
			<div
				ref={scrollRef}
					className="siiv-scroll"
				style={{
					flex: '1 1 0',
					minHeight: 0,
					overflowY: 'auto',
					overscrollBehavior: 'contain',
					padding: '1.25rem',
					// El hueco de la barra existe siempre, haya scroll o no: si aparece y
					// desaparece, las tarjetas se corren unos pixeles al filtrar.
					scrollbarGutter: 'stable',
				}}
			>
				{filteredInvestigators.length === 0 ? (
					<div className="rounded-[10px] border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
						No encontramos investigadores con esos filtros.
					</div>
				) : (
					<>
						{/* ── Primer lote, servido en el HTML (R59) ──
						    La virtualización no monta NADA sin haber medido el contenedor, y
						    en el servidor no hay nada que medir: el HTML del directorio salía
						    con la barra, el pie y un hueco. Nadie sin JavaScript —ni un
						    rastreador que no lo ejecute— veía un solo perfil, en la página
						    que es la puerta de entrada a los 270.
						    Este lote se renderiza plano, con `auto-fill` resolviendo las
						    columnas en CSS (no hace falta saber cuántas entran), y el
						    virtualizador lo releva al hidratar. El primer render del cliente
						    es idéntico al del servidor —`hydrated` arranca en `false` en los
						    dos— así que no hay desajuste de hidratación. */}
						{view === 'grid' && !hydrated && (
							<div
								className="grid gap-4"
								style={{
									gridTemplateColumns: `repeat(auto-fill, minmax(${card.width}px, 1fr))`,
								}}
							>
								{visibleInvestigators.slice(0, SSR_CARD_COUNT).map((inv) => (
									<DirectoryCard key={inv.slug} inv={inv} card={card} />
								))}
							</div>
						)}

						{view === 'grid' && hydrated && (
							<div
								style={{
									height: `${gridVirtualizer.getTotalSize()}px`,
									position: 'relative',
									width: '100%',
								}}
							>
								{gridVirtualizer.getVirtualItems().map((virtualRow) => {
									const start = virtualRow.index * columnCount;
									const rowItems = visibleInvestigators.slice(start, start + columnCount);
									return (
										<div
											key={virtualRow.key}
											data-index={virtualRow.index}
											ref={gridVirtualizer.measureElement}
											className="grid gap-4"
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												transform: `translateY(${virtualRow.start}px)`,
												gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
												paddingBottom: `${GRID_GAP}px`,
											}}
										>
											{rowItems.map((inv) => (
												<DirectoryCard key={inv.slug} inv={inv} card={card} />
											))}
										</div>
									);
								})}
							</div>
						)}

						{view === 'table' && (
							<section className="overflow-hidden rounded-[10px] border border-slate-200 bg-white">
								{/* Header sticky (fuera del área virtualizada), columnas fijas. */}
								<div
									className="sticky top-0 z-10 grid bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500"
									style={{ gridTemplateColumns: TABLE_COLUMNS }}
								>
									<div className="px-4 py-3">Investigador</div>
									<div className="px-4 py-3">Unidad</div>
									<div className="px-4 py-3">Facultad</div>
									<div className="px-4 py-3">ORCID</div>
									<div className="px-4 py-3" />
								</div>
								{/* Cuerpo virtualizado: mismas columnas fijas por fila → sin corrimiento. */}
								<div
									ref={tableBodyRef}
									style={{ height: `${tableVirtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}
								>
									{tableVirtualizer.getVirtualItems().map((virtualRow) => {
										const inv = visibleInvestigators[virtualRow.index];
										return (
											// La fila NO es un botón, y eso es deliberado: una tabla se
											// lee y se copia, y con toda la fila clicable seleccionar un
											// ORCID para pegarlo en otro lado dispara una navegación. El
											// enlace vive en su propia columna, donde además se ve que
											// existe.
											<div
												key={inv.slug}
												data-index={virtualRow.index}
												ref={tableVirtualizer.measureElement}
												className="group grid border-t border-slate-100 text-sm transition hover:bg-slate-50"
												style={{
													position: 'absolute',
													top: 0,
													left: 0,
													width: '100%',
													transform: `translateY(${virtualRow.start - tableScrollMargin}px)`,
													gridTemplateColumns: TABLE_COLUMNS,
												}}
											>
												<div className="flex items-center gap-2 px-4 py-3 font-medium text-slate-800"><span className="truncate">{inv.displayName}</span><TipoInvestigadorBadge tipo={inv.tipoInvestigador} nombre={inv.rolInvestigadorNombre} /></div>
												<div className="truncate px-4 py-3 text-slate-600" title={inv.unit}>{inv.unit}</div>
												<div className="truncate px-4 py-3 text-slate-600" title={inv.macroUnit ?? ''}>{inv.macroUnit}</div>
												<div className="truncate px-4 py-3 text-slate-600">{inv.orcid}</div>
												<div className="flex items-center px-4 py-3">
													<Link
														href={`/investigadores/${inv.slug}`}
														className="inline-flex cursor-pointer items-center rounded-[8px] border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#233a4d]/30 hover:bg-slate-50 hover:text-slate-800"
													>
														Ver perfil
													</Link>
												</div>
											</div>
										);
									})}
								</div>
							</section>
						)}

						{view === 'compact' && (
							<div
								style={{
									height: `${listVirtualizer.getTotalSize()}px`,
									position: 'relative',
									width: '100%',
								}}
							>
								{listVirtualizer.getVirtualItems().map((virtualRow) => {
									const inv = visibleInvestigators[virtualRow.index];
									return (
										<div
											key={inv.slug}
											data-index={virtualRow.index}
											ref={listVirtualizer.measureElement}
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												transform: `translateY(${virtualRow.start}px)`,
												paddingBottom: '12px',
											}}
										>
											{/* La fila ENTERA es el enlace y no lleva botón: ya lo era —el
											    `<Link>` envuelve todo— así que "Ver perfil" repetía a la derecha
											    lo que hacía cualquier punto de la fila. Tampoco lleva flecha: era
											    la misma indicación por segunda vez, y ese lugar rinde más con
											    datos. Que se puede pulsar lo dice el fondo al pasar por encima.
											    Dos columnas, y cada una con dos renglones que se corresponden:
											    a la izquierda quién es y de dónde; a la derecha, qué es y cuánto
											    tiene. */}
											<Link
												href={`/investigadores/${inv.slug}`}
												className="group flex items-center gap-3 rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#233a4d]/25 hover:bg-slate-50"
											>
												<div className="flex min-w-0 flex-1 items-center gap-3">
													<div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[#F5F6FA] text-[#233a4d]">
														<ProfilePhotoImg
															url={inv.fotoPerfilUrl}
															alt={inv.fullName || inv.displayName}
															oculta={inv.fotoPerfilOculta}
															imgClassName="h-full w-full rounded-[10px] object-cover"
															fallback={<UserRound className="h-5 w-5" />}
															badgeSize={10}
														/>
													</div>
													<div className="min-w-0">
														<p className="truncate text-sm font-semibold text-slate-800">
															{inv.displayName}
														</p>
														{/* Abreviada cuando el nombre completo no entra, igual que
														    en la tarjeta y en la portada: en una sola línea, "Centro
														    Regional de Investigación en Desarrollo Sustentable de
														    Atacama" se corta a la mitad y no dice nada. */}
														<p className="truncate text-xs text-slate-500" title={inv.unit}>
															{nombreUnidad(inv)}
														</p>
													</div>
												</div>
												<div className="flex shrink-0 flex-col items-end gap-1">
													<TipoInvestigadorBadge
														tipo={inv.tipoInvestigador}
														nombre={inv.rolInvestigadorNombre}
													/>
													{/* Sólo si hay alguna: repetido "0 líneas" en todas las filas
													    es la misma nada dicha muchas veces, igual que en la
													    tarjeta. Al mismo cuerpo que el badge, para que la columna
													    derecha se lea como un bloque y no como dos cosas sueltas. */}
													{inv.researchLineSlugs.length > 0 ? (
														<span className="whitespace-nowrap text-[11px] font-medium leading-none text-slate-400">
															{inv.researchLineSlugs.length}{' '}
															{inv.researchLineSlugs.length === 1 ? 'línea' : 'líneas'}
														</span>
													) : null}
												</div>
											</Link>
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
			</div>

			{/* ── Footer bar ── */}
			<div
				style={{
					flexShrink: 0,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '0.75rem',
					position: 'relative',
					borderTop: '1px solid #f1f5f9',
					backgroundColor: '#ffffff',
					// El mismo hueco que la barra de arriba: traía 0.625rem, y con la barra
					// en 0.75rem los dos extremos de la vista ni siquiera coincidían entre
					// ellos.
					padding: '1rem 1.25rem',
					paddingRight: 'calc(1.25rem + var(--siiv-scrollbar, 0px))',
					fontSize: '0.75rem',
					color: '#64748b',
					borderRadius: '0 0 10px 10px',
				}}
			>
				{/* Zoom, sólo en la grilla. Vive en el pie porque el panel de filtros
				    dejó de ser un carril permanente: sin una columna fija donde ponerlo,
				    el pie es el único sitio de la vista que está siempre presente y no
				    le quita alto a la grilla. */}
				<div className="hidden items-center gap-2 sm:flex" style={{ minWidth: 0, flex: '0 0 auto' }}>
					{view === 'grid' && (
						<label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
							Zoom
							<input
								type="range"
								min={ZOOM_MIN}
								max={ZOOM_MAX}
								step={ZOOM_STEP}
								value={zoom}
								onChange={(e) => setZoom(Number(e.target.value))}
								className="w-24 accent-[#233a4d]"
							/>
							<button
								type="button"
								onClick={() => setZoom(ZOOM_DEFAULT)}
								title="Volver al 100 %"
								className="cursor-pointer rounded-[8px] border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
								style={{ padding: '2px 8px', fontSize: '11px' }}
							>
								{zoom}%
							</button>
						</label>
					)}
				</div>

				{/* Arriba, dentro del buscador, va cuántos resultados dejó la consulta;
				    acá, cuántos hay en total. Son dos números distintos y por eso están en
				    dos lugares: el de arriba cambia con cada filtro y acompaña a lo que lo
				    mueve, éste es la referencia fija contra la que se mide. Mostrarlos
				    juntos como "12/269" obligaba a leer una fracción para entender algo
				    que dicho por separado se entiende solo. */}
				{/* Centrado respecto de la BARRA, no del hueco que queda entre el zoom y
				    la paginación: esos dos lados pesan distinto, así que un `text-center`
				    dentro del flujo lo dejaba corrido. Posicionado absoluto queda en el
				    medio de verdad, y `pointer-events: none` evita que se coma clics de los
				    controles que tiene debajo. Se oculta en pantallas angostas, donde no
				    hay lugar para un tercer bloque en la misma fila. */}
				<div className="flex-1" />
				<div className="pointer-events-none absolute left-1/2 hidden -translate-x-1/2 text-xs font-medium text-slate-500 sm:block">
					<span className="tabular-nums">{investigators.length}</span> investigadores
				</div>

				{/* Right: page size + pagination */}
				<div className="flex items-center gap-3" style={{ flex: '0 0 auto' }}>
					{/* Cuántos por página: fuera del teléfono. Son cuatro botones que en
					    390 px no entran junto al rango y las flechas, y el tamaño de página
					    es una preferencia que se toca una vez, no en cada consulta. El pie
					    queda con la paginación sola, que es lo único que se usa scrolleando. */}
					<div className="hidden items-center gap-1.5 sm:flex">
						<span className="text-xs font-medium text-slate-500">Mostrar</span>
						{PAGE_SIZE_OPTIONS.map((size) => (
							<button
								key={size}
								type="button"
								onClick={() => handlePageSizeChange(size)}
								className={`cursor-pointer rounded-[8px] border px-2 py-0.5 text-[11px] font-medium transition ${
									pageSize === size
										? 'border-[#233a4d] bg-[#233a4d] text-white'
										: 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
								}`}
							>
								{size === 0 ? 'Todos' : size}
							</button>
						))}
					</div>

					{/* El rango y las flechas se muestran SIEMPRE, aunque con "Todos" no
					    haya nada que paginar: apareciendo y desapareciendo, el pie se
					    recomponía y todo lo de la derecha pegaba un salto cada vez que se
					    cambiaba el tamaño de página. Deshabilitadas dicen lo mismo sin
					    mover nada. */}
					{totalItems > 0 && (
						<>
							{/* El separador es una LÍNEA DIBUJADA, no un guión tipográfico, y
							    ése es el punto: a 11 px el carácter "–" tiene tan poca masa
							    que se pierde entre dos cifras, y subirle el peso lo emborrona
							    en vez de marcarlo — se probaron las dos cosas. Un borde de un
							    pixel se ve exactamente del color y el grosor que se le pide,
							    sin depender de cómo la tipografía suavice el trazo. */}
							<span className="inline-flex items-center gap-1.5 text-[11px] tabular-nums text-slate-600">
								{rangeStart}
								<span className="inline-block h-px w-2 bg-slate-500" aria-hidden="true" />
								{rangeEnd} de {totalItems}
							</span>
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={() => setPage((p) => Math.max(1, p - 1))}
									disabled={pageSize === 0 || safePage <= 1}
									aria-label="Página anterior"
									className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-[6px] border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35"
								>
									<ChevronLeft className="h-3.5 w-3.5" />
								</button>
								<button
									type="button"
									onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
									disabled={pageSize === 0 || safePage >= totalPages}
									aria-label="Página siguiente"
									className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-[6px] border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35"
								>
									<ChevronRight className="h-3.5 w-3.5" />
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</section>
		</>
	);
}

interface ViewButtonProps {
	active: boolean;
	label: string;
	onClick: () => void;
	Icon: ComponentType<{ className?: string }>;
}

function ViewButton({ active, label, onClick, Icon }: ViewButtonProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			// En el teléfono queda SÓLO el icono: con el texto, los botones de vista se
			// llevaban una fila entera para sí. El nombre no se pierde — sigue en
			// `aria-label` para los lectores de pantalla y en `title` al mantener
			// pulsado — y son tres formas de ver una lista, que se reconocen por su
			// icono mucho antes que por su nombre.
			aria-label={label}
			title={label}
			// Cuadrado en el teléfono —el mismo alto de la barra como ancho— y con
			// texto desde `sm`. Un botón de icono con padding lateral de texto queda
			// rectangular sin razón: al no haber etiqueta, el ancho lo debe fijar el
			// alto, no un `px` heredado.
			className={`${CONTROL_CLASS} inline-flex w-[var(--siiv-control-height,36px)] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border px-0 text-xs font-semibold transition sm:w-auto sm:px-3 ${
				active
					? 'border-[#233a4d] bg-[#233a4d] text-white'
					: 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
			}`}
		>
			<Icon className="h-3.5 w-3.5" />
			<span className="hidden sm:inline">{label}</span>
		</button>
	);
}

interface DirectoryCardProps {
	inv: InvestigatorRecord;
	card: DirectoryCardMetrics;
}

/**
 * La tarjeta de la grilla. Está extraída porque se renderiza desde DOS lugares: el
 * virtualizador (después de hidratar) y el primer lote que se sirve en el HTML
 * (antes). Si el markup viviera en uno solo de los dos, el HTML servido y el
 * hidratado dejarían de coincidir.
 */
function DirectoryCard({ inv, card }: DirectoryCardProps) {
	return (
								<Link
									key={inv.slug}
									href={`/investigadores/${inv.slug}`}
									className="relative flex flex-col items-center overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm transition-all duration-200 ease-out hover:border-[#233a4d]/25 hover:shadow"
									style={{
										padding: `${card.padding}px`,
										// Alto EXACTO, no mínimo: es el número que `estimateSize`
										// le promete al virtualizador. Una tarjeta que se estira
										// desalinea el scroll.
										height: `${card.height}px`,
										gap: `${card.gap}px`,
									}}
								>
									{/* Contador de líneas: badge en la esquina, y SÓLO si hay
									    alguna. Antes gastaba una línea propia en todas las
									    tarjetas para decir "0 líneas activas" — hoy ningún
									    perfil tiene líneas cargadas, así que esa línea era
									    ruido repetido 270 veces. Cuando haya datos, el badge
									    aparece solo y sin costarle alto a la tarjeta. */}
									{inv.researchLineSlugs.length > 0 ? (
										<span
											className="absolute rounded-full border border-slate-200 bg-slate-50 font-medium text-slate-500"
											style={{
												top: `${card.padding * 0.55}px`,
												right: `${card.padding * 0.55}px`,
												padding: `${card.textGap * 0.45}px ${card.textGap * 1.3}px`,
												fontSize: `${card.countFont}px`,
												lineHeight: 1,
											}}
										>
											{inv.researchLineSlugs.length}{' '}
											{inv.researchLineSlugs.length === 1 ? 'línea' : 'líneas'}
										</span>
									) : null}
									<div
										className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[#F5F6FA] text-[#233a4d] transition-all duration-200 ease-out"
										style={{ height: `${card.photo}px`, width: `${card.photo}px` }}
									>
										<ProfilePhotoImg
											url={inv.fotoPerfilUrl}
											alt={inv.fullName || inv.displayName}
											oculta={inv.fotoPerfilOculta}
											imgClassName="h-full w-full rounded-[10px] object-cover"
											fallback={
												<UserRound
													className="transition-all duration-200 ease-out"
													style={{ height: `${card.photoIcon}px`, width: `${card.photoIcon}px` }}
												/>
											}
										/>
									</div>
									<div
										className="flex w-full min-w-0 flex-col text-center"
										style={{ gap: `${card.textGap}px` }}
									>
										<h3
											className="truncate font-semibold text-slate-800"
											style={{ fontSize: `${card.nameFont}px`, lineHeight: 1.3 }}
										>
											{inv.displayName}
										</h3>
										{/* El hueco del badge se reserva SIEMPRE, haya estamento o
										    no: una tarjeta más baja que sus vecinas rompería el
										    alto uniforme del que depende `estimateSize`. */}
										<div
											className="flex items-center justify-center"
											style={{ minHeight: `${card.badgeRow}px` }}
										>
											<TipoInvestigadorBadge
												tipo={inv.tipoInvestigador}
												nombre={inv.rolInvestigadorNombre}
												fontSize={card.badgeFont}
											/>
										</div>
										{/* Nombre abreviado cuando el completo no entra (la misma
										    regla que la portada), y dos líneas reservadas como red
										    de seguridad para una unidad sin abreviatura cargada. */}
										<p
											className="line-clamp-2 text-slate-600"
											style={{
												fontSize: `${card.unitFont}px`,
												lineHeight: 1.375,
												minHeight: `${card.unitBlock}px`,
											}}
											title={inv.unit}
										>
											{nombreUnidad(inv)}
										</p>
									</div>
								</Link>
	);
}

interface PanelToggleProps {
	label: string;
	Icon: ComponentType<{ className?: string }>;
	checked: boolean;
	onChange: (checked: boolean) => void;
}

function PanelToggle({ label, Icon, checked, onChange }: PanelToggleProps) {
	return (
		<label className="flex cursor-pointer items-center gap-2 rounded-[10px] border border-slate-200 px-3 py-2 text-sm text-slate-700 transition hover:border-slate-300">
			<input
				type="checkbox"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
				className="h-4 w-4 accent-[#233a4d]"
			/>
			<Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
			{label}
		</label>
	);
}

type PanelSelectOption = string | { value: string; label: string };

interface PanelSelectProps {
	label: string;
	Icon: ComponentType<{ className?: string }>;
	value: string;
	onChange: (value: string) => void;
	options: readonly PanelSelectOption[];
	allLabel: string;
	hint?: string;
	/** Qué decir cuando el catálogo llega vacío, en vez de mostrar un selector con
	 *  una sola opción y dejar al visitante pensando que se rompió. */
	emptyHint?: string;
	renderOption?: (option: SelectOption) => ReactNode;
	getOptionSearchText?: (option: SelectOption) => string;
	menuMinWidth?: number;
}

function PanelSelect({
	label,
	Icon,
	value,
	onChange,
	options,
	allLabel,
	hint,
	emptyHint,
	renderOption,
	getOptionSearchText,
	menuMinWidth,
}: PanelSelectProps) {
	const normalized = options.map((option) =>
		typeof option === 'string' ? { value: option, label: option } : option,
	);
	const isEmpty = normalized.length === 0;

	return (
		// Más chica y en gris que el título del panel: con el mismo cuerpo y color
		// los dos, "Filtros" y "Facultad" se leían como el mismo nivel.
		<label className="flex min-w-0 flex-col gap-1.5 text-xs font-medium text-slate-500">
			<span className="inline-flex items-center gap-1.5">
				<Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
				{label}
			</span>
			<SelectInput
				value={value}
				onChange={onChange}
				options={[{ value: 'all', label: allLabel }, ...normalized]}
				disabled={isEmpty}
				// Con más de una pantalla de opciones, buscar es más rápido que
				// recorrer: 28 unidades no se leen de un vistazo.
				searchable={normalized.length > 8}
				menuMaxHeight="70vh"
				menuMinWidth={menuMinWidth}
				renderOption={renderOption}
				getOptionSearchText={getOptionSearchText}
			/>
			{isEmpty && emptyHint ? (
				<span className="text-[11px] font-normal text-slate-400">{emptyHint}</span>
			) : null}
			{!isEmpty && hint ? (
				<span className="text-[11px] font-normal text-slate-400">{hint}</span>
			) : null}
		</label>
	);
}


/**
 * Una opción de línea en el menú: UNA línea con elipsis, el nombre y, atenuada, la unidad dueña. El texto
 * completo va en `title` (tooltip nativo, con su retraso): no se abre al enfocar con el teclado, y es el
 * límite conocido de usar `title` en vez de un tooltip propio.
 */
function LineOptionLabel({ label, unit, title }: { label: string; unit: string; title: string }) {
	return (
		<span className="block truncate" title={title}>
			{label}
			{unit ? <span className="font-normal text-slate-400"> · {unit}</span> : null}
		</span>
	);
}
